import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  ListSubheader,
  ListItemText,
  ListItemIcon,
  Chip,
  styled,
  alpha,
  Fade,
  Stack,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';

const SelectorPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.grey[500], 0.05),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
     borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-icon': {
    transition: 'transform 0.2s ease-in-out',
    color: theme.palette.text.secondary,
  },
  '& .MuiSelect-iconOpen': {
    transform: 'rotate(180deg)',
  },
  minWidth: 280,
  maxWidth: 400,
}));

const SelectMenuPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  marginTop: theme.spacing(0.5),
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: alpha(theme.palette.background.paper, 0.97),
  backdropFilter: 'blur(8px)',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.2, 2),
  borderRadius: theme.shape.borderRadius * 0.75,
  margin: theme.spacing(0.5, 1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 500,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
  },
  transition: 'background-color 0.15s ease-in-out',
}));

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    margin: theme.spacing(0, 0, 0.5, 0),
    lineHeight: '1.4rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    '&:first-of-type': {
        borderTop: 'none',
        marginTop: 0,
    }
}));

const getTripType = (trip) => (trip.type?.toLowerCase().includes('backhaul') ? 'Backhaul' : 'Dropoff');

const TripSelector = ({ trips = [], selectedTrip, onSelectTrip }) => {

  const groupedTrips = (trips || []).reduce((acc, trip) => {
    const tripIdStr = String(trip.id || '');
    const baseId = tripIdStr.split('-')[0];
    if (!acc[baseId]) {
      acc[baseId] = [];
    }
    acc[baseId].push(trip);
    return acc;
  }, {});

  const sortedGroupKeys = Object.keys(groupedTrips).sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (isNaN(numA) && isNaN(numB)) return a.localeCompare(b);
    if (isNaN(numA)) return 1;
    if (isNaN(numB)) return -1;
    return numA - numB;
  });

  sortedGroupKeys.forEach(key => {
    groupedTrips[key].sort((a, b) => {
        const aType = getTripType(a);
        const bType = getTripType(b);
        if (aType === 'Dropoff' && bType === 'Backhaul') return -1;
        if (aType === 'Backhaul' && bType === 'Dropoff') return 1;
        return String(a.id || '').localeCompare(String(b.id || ''));
    });
  });


  const renderMenuItems = () => {
    const items = [];
    if (!trips || trips.length === 0) {
        return <MenuItem disabled>No trips available</MenuItem>;
    }
    sortedGroupKeys.forEach(baseId => {
      const group = groupedTrips[baseId];
      const dropoffCount = group.filter(t => getTripType(t) === 'Dropoff').length;
      const backhaulCount = group.filter(t => getTripType(t) === 'Backhaul').length;

      items.push(
        <StyledListSubheader key={`subheader-${baseId}`}>
          Trip {baseId}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({dropoffCount > 0 && `${dropoffCount} Dropoff`}{dropoffCount > 0 && backhaulCount > 0 && ', '}{backhaulCount > 0 && `${backhaulCount} Backhaul`})
          </Typography>
        </StyledListSubheader>
      );

      group.forEach(trip => {
        const itemKey = trip.uniqueKey || trip.id || `trip-${Math.random()}`;
        const tripType = getTripType(trip);
        const typeColor = tripType === 'Backhaul' ? 'error' : 'primary';

        items.push(
          <StyledMenuItem key={itemKey} value={itemKey}>
            <ListItemIcon sx={{ minWidth: 32 }}>
               <CircleIcon sx={(theme) => ({ fontSize: 10, color: theme.palette[typeColor].light })} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                   <Typography variant="body2" component="span">
                      {trip.id || 'N/A'}
                   </Typography>
                   <Chip
                     label={tripType}
                     size="small"
                     variant="outlined"
                     color={typeColor}
                     sx={{ height: 20, fontSize: '0.7rem', borderRadius: '4px' }}
                   />
                </Stack>
              }
              secondary={`Segments: ${Array.isArray(trip.segments) ? trip.segments.length : 0}`}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
            />
          </StyledMenuItem>
        );
      });
    });
    return items;
  };

  return (
    <SelectorPaper elevation={0} variant="outlined">
       <LocalShippingIcon
          color="primary"
          sx={{
             fontSize: 26,
             p: 0.8,
             borderRadius: '50%',
             backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
          }}
       />
       <Typography variant="subtitle1" fontWeight="500" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
          Selected Trip:
       </Typography>
       <FormControl sx={{ flexGrow: 1 }} size="small">
         {!selectedTrip && <InputLabel id="trip-selector-label" sx={{ pl: 1.5 }}>Select a Trip</InputLabel>}
         <StyledSelect
            labelId="trip-selector-label"
            label={selectedTrip ? undefined : "Select a Trip"}
            value={selectedTrip ? selectedTrip.uniqueKey : ''}
            onChange={(e) => {
                const selectedKey = e.target.value;
                const trip = (trips || []).find(t => t.uniqueKey === selectedKey);
                if (trip) {
                    onSelectTrip(trip);
                } else {
                    onSelectTrip(null);
                }
            }}
            IconComponent={ArrowDropDownCircleOutlinedIcon}
            MenuProps={{
                PaperProps: {
                   component: SelectMenuPaper,
                   elevation: 0,
                },
                MenuListProps: {
                   sx: (theme) => ({
                      paddingTop: 0,
                      paddingBottom: theme.spacing(1),
                      maxHeight: '65vh',
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-track': { bgcolor: 'transparent', },
                      '&::-webkit-scrollbar-thumb': {
                         bgcolor: alpha(theme.palette.grey[400], 0.6),
                         borderRadius: '3px',
                         '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.8), }
                      },
                      '& .MuiListSubheader-root:first-of-type': {
                      }
                   })
                },
                TransitionComponent: Fade,
                TransitionProps: { timeout: 200 }
            }}
            renderValue={(value) => {
                if (!value || !selectedTrip) {
                    return (
                        <Typography variant="body1" color="text.secondary" sx={{ pl: 1.5 }}>
                            Select a Trip
                        </Typography>
                    );
                }

                const tripType = getTripType(selectedTrip);
                const typeColor = tripType === 'Backhaul' ? 'error' : 'primary';

                return (
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                         {selectedTrip.id || 'N/A'}
                      </Typography>
                      <Chip
                         label={tripType}
                         size="small"
                         color={typeColor}
                         variant="filled"
                         sx={{
                            height: 22,
                            fontWeight: 'medium',
                            borderRadius: '4px',
                            color: `${typeColor}.contrastText`,
                            bgcolor: `${typeColor}.main`
                         }}
                      />
                   </Box>
                );
            }}
         >
            {renderMenuItems()}
         </StyledSelect>
       </FormControl>
    </SelectorPaper>
  );
};

export default TripSelector;